import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@material-ui/core';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import { Machine } from '../../../data/Machine';
import { api } from '../../../api';

interface ProductMachineListProps {
    line_uid:string,
    login:string,
}

interface ColumnData {
    dataKey: keyof Machine;
    label: string;
    numeric?: boolean;
    width: number;
}

ProductMachineList.defaultProps = {
};

const useStyle = makeStyles((theme: Theme) => createStyles({
    fixedFirstCol: {
        position: 'sticky',
        left: 0,
    },
}));

const VirtuosoTableComponents: TableComponents<Machine> = {
    Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref} />
    )),
    Table: (props) => (
        <Table {...props} style={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
    ),
    TableHead,
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
        <TableBody {...props} ref={ref} />
    )),
};

function ProductMachineList(props: ProductMachineListProps): React.ReactElement {
    const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        line_uid, login,
    } = props;
    const classes = useStyle();
    const [productMachineList, setProductMachineList] = useState<Machine[]>();
    const [sumTotalQty, setSumTotalQty] = useState<number>(0);
    const [sumOnlineQty, setSumOnlineQty] = useState<number>(0);
    const [sumSparePartsQty, setSumSparePartsQty] = useState<number>(0);
    const [sumPickupPendingQty, setSumPickupPendingQty] = useState<number>(0);
    const [sumUcptInRepairQty, setSumUcptInRepairQty] = useState<number>(0);
    const [sumUcptPendingShippingQty, setSumUcptPendingShippingQty] = useState<number>(0);

    useEffect(() => {
        api.machineState({
            login,
            line_uid,
        }).then((resData) => {
            let sumTotalQtyTmp = 0;
            let sumOnlineQtyTmp = 0;
            let sumSparePartsQtyTmp = 0;
            let sumPickupPendingQtyTmp = 0;
            let sumUcptInRepairQtyTmp = 0;
            let sumUcptPendingShippingQtyTmp = 0;
            resData.forEach((row) => {
                sumTotalQtyTmp += row.sum_machine;
                sumOnlineQtyTmp += row.online;
                sumSparePartsQtyTmp += row.spare;
                sumPickupPendingQtyTmp += row.order_draft;
                sumUcptInRepairQtyTmp += row.repair;
                sumUcptPendingShippingQtyTmp += row.before_finish;
            });

            setSumTotalQty(sumTotalQtyTmp);
            setSumOnlineQty(sumOnlineQtyTmp);
            setSumSparePartsQty(sumSparePartsQtyTmp);
            setSumPickupPendingQty(sumPickupPendingQtyTmp);
            setSumUcptInRepairQty(sumUcptInRepairQtyTmp);
            setSumUcptPendingShippingQty(sumUcptPendingShippingQtyTmp);
            setProductMachineList(resData);
        });
    }, [login, line_uid]);

    const columns: ColumnData[] = [
        {
            width: 170,
            label: '型號',
            dataKey: 'name',
        },
        {
            width: 70,
            label: '總數',
            dataKey: 'sum_machine',
            numeric: true,
        },
        {
            width: 70,
            label: '線上',
            dataKey: 'online',
            numeric: true,
        },
        {
            width: 80,
            label: '備品區',
            dataKey: 'spare',
            numeric: true,
        },
        {
            width: 80,
            label: '待取件',
            dataKey: 'order_draft',
            numeric: true,
        },
        {
            width: 80,
            label: 'UCPT維修中',
            dataKey: 'repair',
            numeric: true,
        },
        {
            width: 80,
            label: 'UCPT待出貨',
            dataKey: 'before_finish',
            numeric: true,
        },
    ];

    function fixedHeaderContent() {
        return (
            <TableRow style={{ backgroundColor: '#fafafa' }}>
                <TableCell
                    className={classes.fixedFirstCol}
                    style={{ backgroundColor: '#fafafa' }}
                    width="160"
                >
                    型號

                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                    width="70"
                >
                    <div>客戶</div>
                    <div>總數</div>
                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                    width="70"
                >
                    <div>客戶</div>
                    <div>線上</div>
                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                    width="80"
                >
                    <div>客戶</div>
                    <div>備品區</div>
                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                    width="80"
                >
                    <div>客戶</div>
                    <div>待送修</div>
                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                    width="80"

                >
                    <div>UCPT</div>
                    <div>維修中</div>
                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                    width="80"

                >
                    <div>UCPT</div>
                    <div>待出貨</div>
                </TableCell>
            </TableRow>
        );
    }

    function rowContent(_index: number, row: Machine) {
        return (
            <>
                {columns.map((column, index) => (
                    <TableCell
                        key={column.dataKey}
                        align={column.numeric || false ? 'right' : 'left'}
                        style={{ width: column.width, backgroundColor: '#fff' }}
                        className={column.dataKey === 'name' ? classes.fixedFirstCol : ''}
                    >
                        {row[column.dataKey]}
                    </TableCell>
                ))}
            </>
        );
    }

    function fixedFootContent() {
        return (
            <TableRow style={{ backgroundColor: '#fafafa' }}>
                <TableCell className={classes.fixedFirstCol} style={{ backgroundColor: '#fafafa' }}>合計</TableCell>
                <TableCell
                    variant="head"
                    align="right"
                >
                    { sumTotalQty }

                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                >
                    { sumOnlineQty }

                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                >
                    { sumSparePartsQty }

                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                >
                    { sumPickupPendingQty }

                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                >
                    { sumUcptInRepairQty }

                </TableCell>
                <TableCell
                    variant="head"
                    align="right"
                >
                    { sumUcptPendingShippingQty }

                </TableCell>
            </TableRow>
        );
    }

    return (
        <Paper style={{ height: 400, width: '100%' }}>
            <TableVirtuoso
                data={productMachineList}
                components={VirtuosoTableComponents}
                fixedHeaderContent={fixedHeaderContent}
                fixedFooterContent={fixedFootContent}
                itemContent={rowContent}
            />
        </Paper>
    );
}

export { ProductMachineList };
